/*
 * Tells the shopper how much is still missing before a discount code can be
 * used.
 *
 * Shopify never gives the storefront a code's minimum purchase amount: applying
 * a code that is below it comes back as `{ code, applicable: false }` and
 * nothing else. So the thresholds are declared by the merchant in
 * Theme settings -> Cart, and this element reads them from `data-config`.
 */
(function () {
  if (customElements.get('kd-coupon-goal')) return;

  var STORE_KEY = 'kd-coupon-goal-code';

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /*
   * "250", "250,50", "1.250", "1,250.50" — shops write amounts in whichever
   * style they are used to. Whichever separator comes last decides, unless it
   * is followed by exactly three digits, which only ever means thousands.
   */
  function toNumber(raw) {
    var s = String(raw).replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    var lastDot = s.lastIndexOf('.');
    var lastComma = s.lastIndexOf(',');
    var cut = Math.max(lastDot, lastComma);
    if (cut === -1) return parseFloat(s);
    var decimals = s.length - cut - 1;
    if (decimals === 3 && (lastDot === -1 || lastComma === -1)) return parseFloat(s.replace(/[.,]/g, ''));
    return parseFloat(s.slice(0, cut).replace(/[.,]/g, '') + '.' + s.slice(cut + 1));
  }

  // Does this cart line sit in any of the given collections?
  function inAny(line, handles) {
    for (var j = 0; j < handles.length; j++) {
      if (line.handles.indexOf(handles[j]) !== -1) return true;
    }
    return false;
  }

  function money(major) {
    var cents = Math.round(major * 100);
    var fox = window.FoxTheme;
    if (fox && fox.Currency && fox.Currency.formatMoney && fox.settings) {
      return fox.Currency.formatMoney(cents, fox.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  class KdCouponGoal extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[is="cart-discount"]');
      this.input = this.querySelector('input[name="discount"]');
      this.hint = this.querySelector('[data-kd-hint]');
      if (!this.form || !this.input || !this.hint) return;

      this.map = this.parseConfig(this.dataset.config);
      this.subtotal = (Number(this.dataset.cartTotal) || 0) / 100;
      this.lines = this.parseLines(this.dataset.lines);
      this.names = this.parseNames(this.dataset.collectionNames);
      this.linesTrusted = true;

      this.onSubmit = this.handleSubmit.bind(this);
      this.onCartUpdated = this.handleCartUpdated.bind(this);

      /*
       * Capture phase on purpose: the theme's own `cart-discount` handler runs
       * on the bubble phase, and it clears the field and prints a generic
       * "code cannot be applied". Getting in first is what lets the specific
       * message be shown instead.
       */
      this.form.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.restore();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    /*
     * "KD5 = 250" applies to the whole basket.
     * "Honey15 = 100 @ natura, pinkpawpal" counts only lines from those
     * collections, which is how Shopify itself measures the minimum for a code
     * restricted to a collection. Reading it as a basket total is what made the
     * old hint say "you are fine" right before the code was refused.
     * "SALE = 0 @ pinkpawpal" means no minimum, but something eligible must be
     * in the basket.
     * "KD5 = 100 ! meraki-home" is the mirror image: everything counts except
     * those lines. Shopify enforces the same thing by pointing the code at a
     * collection that leaves that vendor out.
     */
    parseConfig(raw) {
      var map = {};

      // Handles written after a marker, stopping at the next marker, so the
      // two lists can be given in either order.
      function listAfter(rest, marker) {
        var i = rest.indexOf(marker);
        if (i === -1) return null;
        var tail = rest.slice(i + 1);
        var stop = tail.search(/[@!]/);
        if (stop !== -1) tail = tail.slice(0, stop);
        var out = tail.split(',')
          .map(function (h) { return h.trim().toLowerCase(); })
          .filter(Boolean);
        return out.length ? out : null;
      }

      String(raw || '').split(/[\r\n;]+/).forEach(function (line) {
        if (/^\s*#/.test(line)) return; // a line the merchant left as a note
        var m = line.match(/^\s*([^\s=:]+)\s*[=:\s]\s*(.+?)\s*$/);
        if (!m) return;

        var code = m[1].trim();
        var rest = m[2];

        var collections = listAfter(rest, '@');
        var excludes = listAfter(rest, '!');

        var firstMarker = rest.search(/[@!]/);
        var amount = toNumber(firstMarker === -1 ? rest : rest.slice(0, firstMarker));
        if (isNaN(amount)) amount = 0;
        map[code.toUpperCase()] = {
          amount: amount,
          label: code,
          collections: collections,
          excludes: excludes,
        };
      });
      return map;
    }

    // "variantId:lineTotalInBani:handle,handle;" — rendered by Liquid and
    // refreshed whenever the drawer re-renders.
    parseLines(raw) {
      return String(raw || '').split(';').map(function (chunk) {
        if (!chunk) return null;
        var parts = chunk.split(':');
        if (parts.length < 3) return null;
        return {
          id: String(parts[0]),
          total: (Number(parts[1]) || 0) / 100,
          handles: parts[2].split(',').filter(Boolean),
        };
      }).filter(Boolean);
    }

    // "handle=Nume|handle=Nume" so the hint names collections the way the shop does.
    parseNames(raw) {
      var out = {};
      String(raw || '').split('|').forEach(function (pair) {
        var i = pair.indexOf('=');
        if (i > 0) out[pair.slice(0, i).trim()] = pair.slice(i + 1).trim();
      });
      return out;
    }

    /*
     * What the code can actually be measured against. Returns null when the
     * rendered lines cannot be trusted — a freshly added product the server has
     * not described yet — because a wrong number is worse than no message:
     * staying quiet hands the question back to Shopify, which answers correctly.
     */
    eligible(code) {
      var entry = this.map[code];
      if (!entry) return null;
      if (!entry.collections && !entry.excludes) return this.subtotal;
      if (!this.linesTrusted) return null;

      var total = 0;
      for (var i = 0; i < this.lines.length; i++) {
        var line = this.lines[i];
        if (entry.excludes && inAny(line, entry.excludes)) continue;
        if (entry.collections && !inAny(line, entry.collections)) continue;
        total += line.total;
      }
      return total;
    }

    // True when the code does not simply apply to the whole basket.
    restricted(code) {
      var entry = this.map[code];
      return !!(entry && (entry.collections || entry.excludes));
    }

    joinNames(handles, word) {
      if (!handles || !handles.length) return '';
      var self = this;
      var names = handles.map(function (h) { return self.names[h] || h; });
      if (names.length === 1) return names[0];
      return names.slice(0, -1).join(', ') + ' ' + (word || 'sau') + ' ' + names[names.length - 1];
    }

    collectionNames(code) {
      var entry = this.map[code];
      return entry ? this.joinNames(entry.collections, 'sau') : '';
    }

    excludeNames(code) {
      var entry = this.map[code];
      return entry ? this.joinNames(entry.excludes, 'si') : '';
    }

    handleCartUpdated(event) {
      var cart = event && event.detail && event.detail.cart;
      if (!cart) return;
      this.subtotal = (Number(cart.items_subtotal_price) || 0) / 100;
      this.refresh();
    }

    handleSubmit(event) {
      var code = (this.input.value || '').trim().toUpperCase();
      if (!code || !(code in this.map)) return;

      // Our own re-submit, once the amount checked out. Let it through.
      if (this.passed === code) {
        this.passed = null;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      var self = this;
      // The rendered subtotal can be a step behind; ask the cart itself before
      // telling somebody they are short of money.
      var url = ((window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart') + '.js';
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.subtotal = (Number(cart.items_subtotal_price) || 0) / 100;
          // A line the server has not described yet means the collection data
          // is incomplete, and any figure drawn from it would be a guess.
          var known = {};
          self.lines.forEach(function (l) { known[l.id] = true; });
          self.linesTrusted = (cart.items || []).every(function (item) { return known[String(item.variant_id || item.id)]; });
        })
        .catch(function () { /* keep the rendered figures */ })
        .then(function () {
          var entry = self.map[code];
          var have = self.eligible(code);

          // Cannot measure it honestly: let Shopify give the verdict.
          if (have === null) { self.clear(); self.passed = code; self.form.requestSubmit(); return; }

          if (self.restricted(code) && have === 0) {
            self.only(code);
            return;
          }
          var missing = entry.amount - have;
          if (missing > 0) {
            self.show(code, missing);
            return;
          }
          self.clear();
          self.passed = code;
          self.form.requestSubmit();
        });
    }

    // The drawer replaces its own markup on every cart change, which takes the
    // message with it. Remembering the code brings it back with the new total.
    restore() {
      var code;
      try { code = sessionStorage.getItem(STORE_KEY); } catch (e) { code = null; }
      if (!code || !(code in this.map)) return;
      this.active = code;
      this.refresh();
    }

    refresh() {
      if (!this.active) return;
      var entry = this.map[this.active];
      var have = this.eligible(this.active);
      if (have === null) return;
      if (this.restricted(this.active) && have === 0) { this.only(this.active, true); return; }
      var missing = entry.amount - have;
      if (missing > 0) {
        this.show(this.active, missing, true);
      } else {
        this.reached(this.active);
      }
    }

    // The map is keyed uppercase for matching; the label carries the real spelling.
    label(code) {
      var entry = this.map[code];
      return (entry && entry.label) || code;
    }

    // Says what is missing AND where it has to come from. "Add 3,04 lei" is
    // useless advice when only Natura products move that number.
    only(code, quiet) {
      var entry = this.map[code];
      var template, where;
      if (entry && entry.collections) {
        template = this.dataset.only || 'Codul [cod] se aplică doar la produsele din [colectii].';
        where = this.collectionNames(code);
      } else {
        // Nothing but exclusions: say what the code does NOT cover.
        template = this.dataset.except || 'Codul [cod] nu se aplică la produsele [colectii].';
        where = this.excludeNames(code);
      }
      this.hint.innerHTML = template
        .replace('[cod]', '<strong>' + escapeHtml(this.label(code)) + '</strong>')
        .replace('[colectii]', '<strong>' + escapeHtml(where) + '</strong>');
      this.hint.classList.remove('kd-coupon__hint--done');
      this.hint.hidden = false;
      this.active = code;
      try { sessionStorage.setItem(STORE_KEY, code); } catch (e) { /* private mode */ }
      if (!quiet) this.input.focus();
    }

    show(code, missing, quiet) {
      var template = this.dataset.message || 'Mai adaugă [suma] ca să poți folosi codul [cod].';
      var amount = money(missing);
      var where = this.collectionNames(code);
      if (where) amount = amount + ' din ' + escapeHtml(where);
      this.hint.innerHTML = template
        .replace('[suma]', '<strong>' + amount + '</strong>')
        .replace('[cod]', '<strong>' + escapeHtml(this.label(code)) + '</strong>');
      this.hint.classList.remove('kd-coupon__hint--done');
      this.hint.hidden = false;
      this.active = code;
      try { sessionStorage.setItem(STORE_KEY, code); } catch (e) { /* private mode */ }
      if (!quiet) this.input.focus();
    }

    reached(code) {
      var template = this.dataset.reached || 'Gata, poți folosi codul [cod].';
      this.hint.innerHTML = template.replace('[cod]', '<strong>' + escapeHtml(this.label(code)) + '</strong>');
      this.hint.classList.add('kd-coupon__hint--done');
      this.hint.hidden = false;
      // Cleared from storage so the success line shows once, not on every render.
      this.active = null;
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* private mode */ }
    }

    clear() {
      this.hint.hidden = true;
      this.hint.innerHTML = '';
      this.active = null;
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* private mode */ }
    }
  }

  customElements.define('kd-coupon-goal', KdCouponGoal);
})();
